import type {
	ControllerProps,
	FieldPath,
	FieldValues,
	FormProviderProps,
	SubmitHandler,
} from 'react-hook-form';
import { cn } from '@lqa-frontend/ui-system';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
	Controller,
	FormProvider,
	useFormContext,
	useFormState,
} from 'react-hook-form';
import { Label } from './Label';
import type { ILabelProps } from './Label';
import { Icon } from '../icon';
import { BodyS } from '../typography';

type IFormProps<
	TFieldValues extends FieldValues,
	TTransformedValues extends FieldValues = TFieldValues,
> = {
	onSubmit?: TTransformedValues extends undefined
		? SubmitHandler<TFieldValues>
		: TTransformedValues extends FieldValues
			? SubmitHandler<TTransformedValues>
			: never;
	className?: string;
};

const Form = <
	TFieldValues extends FieldValues,
	TContext = unknown,
	TTransformedValues extends FieldValues = TFieldValues,
>({
	onSubmit,
	...props
}: FormProviderProps<TFieldValues, TContext, TTransformedValues> &
	IFormProps<TFieldValues, TTransformedValues>) => (
	<FormProvider {...props}>
		<form
			onSubmit={onSubmit ? props.handleSubmit(onSubmit) : undefined}
			className={cn('flex flex-col gap-24', props.className)}
		>
			{props.children}
		</form>
	</FormProvider>
);

type FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
	name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
	{} as FormFieldContextValue
);

const FormField = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	...props
}: ControllerProps<TFieldValues, TName>) => (
	<FormFieldContext.Provider value={{ name: props.name }}>
		<Controller {...props} />
	</FormFieldContext.Provider>
);

const useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState } = useFormContext();
	const formState = useFormState({ name: fieldContext.name });
	const fieldState = getFieldState(fieldContext.name, formState);

	if (!fieldContext) {
		throw new Error('useFormField should be used within <FormField>');
	}

	const { id } = itemContext;

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState,
	};
};

type FormItemContextValue = {
	id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
	{} as FormItemContextValue
);

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
	const id = React.useId();

	return (
		<FormItemContext.Provider value={{ id }}>
			<div
				data-slot="form-item"
				className={cn('grid gap-8', className)}
				{...props}
			/>
		</FormItemContext.Provider>
	);
}

function FormLabel({ className, ...props }: ILabelProps) {
	const { error, formItemId } = useFormField();

	return (
		<Label
			data-slot="form-label"
			data-error={!!error}
			className={cn('data-[error=true]:text-danger', className)}
			htmlFor={formItemId}
			{...props}
		/>
	);
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
	const { error, formItemId, formDescriptionId, formMessageId } =
		useFormField();

	return (
		<Slot
			data-slot="form-control"
			id={formItemId}
			aria-describedby={
				!error
					? `${formDescriptionId}`
					: `${formDescriptionId} ${formMessageId}`
			}
			aria-invalid={!!error}
			{...props}
		/>
	);
}

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
	const { formDescriptionId } = useFormField();

	return (
		<p
			data-slot="form-description"
			id={formDescriptionId}
			className={cn('text-neutral text-10 font-light', className)}
			{...props}
		/>
	);
}

function FormMessage({ className, ...props }: React.ComponentProps<'p'>) {
	const { error, formMessageId } = useFormField();
	const body = error ? String(error?.message ?? '') : props.children;

	if (!body) {
		return null;
	}

	return (
		<BodyS
			data-slot="form-message"
			id={formMessageId}
			className={cn('flex items-center gap-4', className)}
			{...props}
			color="danger"
		>
			<Icon name="exclamationCircle" size="xs" />
			{body}
		</BodyS>
	);
}

export {
	useFormField,
	Form,
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
	FormMessage,
	FormField,
};
